import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import FloatingHelp from '@/components/askQuestion/FloatingHelp';
import FarmerAdvisory from '@/components/cropProblem/FarmerAdvisory';
import PhotoSearch from '@/components/cropProblem/PhotoSearch';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import CommentsBar from '@/components/posts/CommentsBar';
import CommunityFeedCard from '@/components/posts/CommunityFeedCard';
import CommentsList from '@/components/posts/PostCommentsList';
import SocialMetaTags from '@/components/SocialMetaTags';
import { ACCESS_TOKEN_KEY, WEBSITE_URL } from '@/constants/common';
import { POST_TYPE } from '@/constants/posts';
import useComments from '@/hooks/usePostComments';
import { Comment as PostComment, CommunityPost } from '@/models/Posts';
import getPostByslug from '@/utils/api/getCommunityFeed';
import { htmlToString, slugToString } from '@/utils/helper';
const CommunityPostPage = (postData: CommunityPost) => {
  const {
    t
  } = useTranslation('posts');
  const {
    locale,
    asPath
  } = useRouter();
  const [commentFocused, setCommentFocused] = useState(false);
  const [commentCount, setCommentCount] = useState<number>(postData.comment_count);
  const {
    comments,
    loading: commentsLoading,
    loadingNext,
    fetchNext,
    error,
    nextPageUrl,
    fetchNewComment
  } = useComments(postData.id);
  const handleAddComment = () => {
    setCommentCount(prev => prev + 1);
  };
  const handleCommentsCallback = (comment: PostComment) => {
    handleAddComment();
    fetchNewComment(comment);
  };
  useEffect(() => {
    if (asPath.includes('#comment')) {
      setCommentFocused(true);
    }
  }, [asPath]);
  const postTitle = slugToString(postData.slug);
  const postDescription = htmlToString(postData.content.text).substring(0, 300);
  const localePrefix = locale === DEFAULT_LANG ? '' : `/${locale}`;
  const canonicalUrl = `${WEBSITE_URL}${localePrefix}/community/post/${postData.slug}`;
  // SocialMetaTags prepends WEBSITE_URL internally, so pass a path only
  const socialUrl = `${localePrefix}/community/post/${postData.slug}`.replace(/^\//, '');
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="[slug].tsx">
        <title>{`${postTitle} | DeHaat Kisan Community`}</title>
        <meta name="description" content={postDescription} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang={`${locale}-in`} href={canonicalUrl} />
      </Head>
      <SocialMetaTags video={(postData.attachments.videos || [])[0]} title={postTitle} description={postDescription} url={socialUrl} data-sentry-element="SocialMetaTags" data-sentry-source-file="[slug].tsx" />
      <main className="bg-neutral-10 min-h-screen">
        <HeaderWithBackButton heading={t('details_caption')} gaEventData={postData.tags.length > 0 ? JSON.stringify(postData.tags) : undefined} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="[slug].tsx" />
        <section className="bg-white mx-auto max-w-7xl">
          <div className={`w-full flex flex-col md:items-center px-4 py-8`}>
            <CommunityFeedCard commentCount={commentCount} className="mt-16" {...postData} data-sentry-element="CommunityFeedCard" data-sentry-source-file="[slug].tsx" />
          </div>
          <div className="mx-auto px-4 md:px-0 pb-8 max-w-4xl">
            <CommentsList id={postData.id} comments={comments} loading={commentsLoading} loadingNext={loadingNext} error={error} fetchNext={fetchNext} nextPageUrl={nextPageUrl} data-sentry-element="CommentsList" data-sentry-source-file="[slug].tsx" />
            <CommentsBar id={postData.id} isFocused={commentFocused} postType={POST_TYPE.Community} callback={handleCommentsCallback} data-sentry-element="CommentsBar" data-sentry-source-file="[slug].tsx" />
          </div>
        </section>
        <section className="bg-white mx-auto my-2 p-4 max-w-7xl">
          <PhotoSearch tags={postData.tags} data-sentry-element="PhotoSearch" data-sentry-source-file="[slug].tsx" />
        </section>
        <section className="bg-white mx-auto my-2 p-4 max-w-7xl">
          <FarmerAdvisory tags={postData.tags} data-sentry-element="FarmerAdvisory" data-sentry-source-file="[slug].tsx" />
        </section>
        <FloatingHelp data-sentry-element="FloatingHelp" data-sentry-source-file="[slug].tsx" />
      </main>
    </>;
};
export async function getServerSideProps({
  req: {
    cookies
  },
  query,
  locale
}: GetServerSidePropsContext) {
  const token = cookies[ACCESS_TOKEN_KEY];
  const response = await getPostByslug(query.slug as string, locale, token);
  if (response) {
    return {
      props: {
        ...response
      }
    };
  }
  return {
    notFound: true
  };
}
export default CommunityPostPage;